import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { KPICard } from '../components/KPICard';
import { SiteFilter, siteParam, useSites } from '../components/SiteFilter';
import { CurrencySelector, type CurrencyMode, currencySymbol } from '../components/CurrencySelector';
import { getGGRReport, getDepositReport, getWithdrawalReport, getActivePlayers } from '../api/client';
import { useDataPilot } from '../components/datapilot/DataPilotProvider';
import { DashboardNarrative } from '../components/datapilot/DashboardNarrative';
import { KPI_GLOSSARY } from '../data/kpi-glossary';
import type { DPNamedSnapshot } from '../api/datapilot';
import { formatAmount, formatNumber } from '../lib/format';

// Maps a dashboard KPI card to its glossary entry + snapshot unit.
const DASH_KPI_META: Record<string, { id: string; unit?: string }> = {
  ggr: { id: 'KPI-X01' },
  ngr: { id: 'KPI-X02' },
  turnover: { id: 'KPI-C27' },
  deposits: { id: 'KPI-C13' },
  withdrawals: { id: 'KPI-C21' },
  activePlayers: { id: 'KPI-C03', unit: 'players' },
  arpu: { id: 'KPI-C04' },
};

type Period = 'today' | 'week' | 'month' | 'quarter';

interface TimeSeriesRow {
  date: string;
  ggr: number;
  ngr: number;
  turnover: number;
}

interface KPIMetrics {
  ggr: { value: number; change: number };
  ngr: { value: number; change: number };
  turnover: { value: number; change: number };
  deposits: { value: number; change: number };
  withdrawals: { value: number; change: number };
  activePlayers: { value: number; change: number };
}

const NATIVE_SYMBOLS: Record<string, string> = {
  EUR: '€', USD: '$', GBP: '£', CHF: 'CHF ', SEK: 'kr', NOK: 'kr',
  DKK: 'kr', PLN: 'zł', CZK: 'Kč', HUF: 'Ft', RON: 'lei ',
  BRL: 'R$', MXN: 'MX$', ARS: 'AR$', CLP: 'CL$', COP: 'CO$',
  PEN: 'S/', ZAR: 'R', SZL: 'E', NGN: '₦', KES: 'KSh ',
  GHS: 'GH₵', TZS: 'TSh ', UGX: 'USh ', INR: '₹', JPY: '¥',
  CNY: '¥', KRW: '₩', THB: '฿', VND: '₫', IDR: 'Rp', MYR: 'RM ',
  PHP: '₱', TRY: '₺', RUB: '₽', UAH: '₴', GEL: '₾', AED: 'د.إ',
  CAD: 'CA$', AUD: 'AU$', NZD: 'NZ$',
};

function nativeSymbol(currency: string): string {
  return NATIVE_SYMBOLS[currency] || `${currency} `;
}

const EMPTY_METRICS: KPIMetrics = {
  ggr: { value: 0, change: 0 },
  ngr: { value: 0, change: 0 },
  turnover: { value: 0, change: 0 },
  deposits: { value: 0, change: 0 },
  withdrawals: { value: 0, change: 0 },
  activePlayers: { value: 0, change: 0 },
};

function getDateRange(period: Period) {
  const now = new Date();
  const today = now.toISOString().split('T')[0];
  let days = 1;
  switch (period) {
    case 'today': days = 1; break;
    case 'week': days = 7; break;
    case 'month': days = 30; break;
    case 'quarter': days = 90; break;
  }
  const from = new Date(now);
  from.setDate(from.getDate() - days);
  const prevFrom = new Date(from);
  prevFrom.setDate(prevFrom.getDate() - days);

  return {
    date_from: from.toISOString().split('T')[0],
    date_to: today,
    prev_from: prevFrom.toISOString().split('T')[0],
    prev_to: from.toISOString().split('T')[0],
  };
}

function sumField(rows: any[], field: string): number {
  return rows.reduce((acc: number, r: any) => acc + (Number(r[field]) || 0), 0);
}

function pctChange(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Number(((current - previous) / Math.abs(previous) * 100).toFixed(1));
}

export const PerformanceDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { allSites } = useSites();
  const { openForMetric } = useDataPilot();
  const [period, setPeriod] = useState<Period>('week');
  const [currencyMode, setCurrencyMode] = useState<CurrencyMode>('EUR');
  const [metrics, setMetrics] = useState<KPIMetrics>(EMPTY_METRICS);
  const [currencyMetrics, setCurrencyMetrics] = useState<Map<string, KPIMetrics>>(new Map());
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [siteIds, setSiteIds] = useState<number[]>([]);

  const symbol = currencySymbol(currencyMode);

  const refreshData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const range = getDateRange(period);
      const siteIdParam = siteParam(allSites, siteIds);
      const dc = currencyMode;

      // Fetch current + previous period in parallel for KPI change calculation
      const [ggrCur, depCur, wdCur, plCur, ggrPrev, depPrev, wdPrev, plPrev] = await Promise.all([
        getGGRReport({ date_from: range.date_from, date_to: range.date_to, site_ids: siteIdParam, display_currency: dc }).catch(() => ({ data: [] })),
        getDepositReport({ date_from: range.date_from, date_to: range.date_to, site_ids: siteIdParam, display_currency: dc }).catch(() => ({ data: [] })),
        getWithdrawalReport({ date_from: range.date_from, date_to: range.date_to, site_ids: siteIdParam, display_currency: dc }).catch(() => ({ data: [] })),
        getActivePlayers({ date_from: range.date_from, date_to: range.date_to, site_ids: siteIdParam }).catch(() => ({ data: [] })),
        getGGRReport({ date_from: range.prev_from, date_to: range.prev_to, site_ids: siteIdParam, display_currency: dc }).catch(() => ({ data: [] })),
        getDepositReport({ date_from: range.prev_from, date_to: range.prev_to, site_ids: siteIdParam, display_currency: dc }).catch(() => ({ data: [] })),
        getWithdrawalReport({ date_from: range.prev_from, date_to: range.prev_to, site_ids: siteIdParam, display_currency: dc }).catch(() => ({ data: [] })),
        getActivePlayers({ date_from: range.prev_from, date_to: range.prev_to, site_ids: siteIdParam }).catch(() => ({ data: [] })),
      ]);

      // Build per-currency metrics for BASE mode
      if (dc === 'BASE') {
        const currencies = new Set<string>();
        for (const r of (ggrCur.data || [])) if (r.currency) currencies.add(r.currency);
        for (const r of (depCur.data || [])) if (r.currency) currencies.add(r.currency);
        for (const r of (wdCur.data || [])) if (r.currency) currencies.add(r.currency);

        const byCurrency = new Map<string, KPIMetrics>();
        for (const ccy of Array.from(currencies).sort()) {
          const filterCcy = (rows: any[]) => (rows || []).filter((r: any) => r.currency === ccy);
          const curG = filterCcy(ggrCur.data); const prevG = filterCcy(ggrPrev.data);
          const curD = filterCcy(depCur.data); const prevD = filterCcy(depPrev.data);
          const curW = filterCcy(wdCur.data); const prevW = filterCcy(wdPrev.data);

          const cGGR = sumField(curG, 'ggr'), pGGR = sumField(prevG, 'ggr');
          const cNGR = sumField(curG, 'ngr'), pNGR = sumField(prevG, 'ngr');
          const cTO = sumField(curG, 'turnover'), pTO = sumField(prevG, 'turnover');
          const cDep = sumField(curD, 'total_deposits'), pDep = sumField(prevD, 'total_deposits');
          const cWd = sumField(curW, 'total_withdrawals'), pWd = sumField(prevW, 'total_withdrawals');
          // Use unique_player_count from GGR data (per-currency) instead of active_players endpoint (no currency)
          const cPl = sumField(curG, 'unique_player_count'), pPl = sumField(prevG, 'unique_player_count');

          byCurrency.set(ccy, {
            ggr: { value: cGGR, change: pctChange(cGGR, pGGR) },
            ngr: { value: cNGR, change: pctChange(cNGR, pNGR) },
            turnover: { value: cTO, change: pctChange(cTO, pTO) },
            deposits: { value: cDep, change: pctChange(cDep, pDep) },
            withdrawals: { value: cWd, change: pctChange(cWd, pWd) },
            activePlayers: { value: cPl, change: pctChange(cPl, pPl) },
          });
        }
        setCurrencyMetrics(byCurrency);

        // Active Players is not currency-specific — compute global totals
        const curPlayers = sumField(plCur.data || [], 'active_players');
        const prevPlayers = sumField(plPrev.data || [], 'active_players');
        setMetrics({
          ...EMPTY_METRICS,
          activePlayers: { value: curPlayers, change: pctChange(curPlayers, prevPlayers) },
        });
      } else {
        setCurrencyMetrics(new Map());

        const curGGR = sumField(ggrCur.data || [], 'ggr');
        const curNGR = sumField(ggrCur.data || [], 'ngr');
        const curTurnover = sumField(ggrCur.data || [], 'turnover');
        const curDeposits = sumField(depCur.data || [], 'total_deposits');
        const curWithdrawals = sumField(wdCur.data || [], 'total_withdrawals');
        const curPlayers = sumField(plCur.data || [], 'active_players');

        const prevGGR = sumField(ggrPrev.data || [], 'ggr');
        const prevNGR = sumField(ggrPrev.data || [], 'ngr');
        const prevTurnover = sumField(ggrPrev.data || [], 'turnover');
        const prevDeposits = sumField(depPrev.data || [], 'total_deposits');
        const prevWithdrawals = sumField(wdPrev.data || [], 'total_withdrawals');
        const prevPlayers = sumField(plPrev.data || [], 'active_players');

        setMetrics({
          ggr: { value: curGGR, change: pctChange(curGGR, prevGGR) },
          ngr: { value: curNGR, change: pctChange(curNGR, prevNGR) },
          turnover: { value: curTurnover, change: pctChange(curTurnover, prevTurnover) },
          deposits: { value: curDeposits, change: pctChange(curDeposits, prevDeposits) },
          withdrawals: { value: curWithdrawals, change: pctChange(curWithdrawals, prevWithdrawals) },
          activePlayers: { value: curPlayers, change: pctChange(curPlayers, prevPlayers) },
        });
      }

      // Time series from GGR daily data — aggregate across sites per date
      const byDate = new Map<string, TimeSeriesRow>();
      for (const r of (ggrCur.data || [])) {
        const key = r.date;
        const existing = byDate.get(key);
        if (existing) {
          existing.ggr += Number(r.ggr) || 0;
          existing.ngr += Number(r.ngr) || 0;
          existing.turnover += Number(r.turnover) || 0;
        } else {
          byDate.set(key, {
            date: key,
            ggr: Number(r.ggr) || 0,
            ngr: Number(r.ngr) || 0,
            turnover: Number(r.turnover) || 0,
          });
        }
      }
      setTimeSeriesData(Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date)));

    } catch (err) {
      console.error('Failed to refresh dashboard:', err);
      setError(t('dashboard.failedToLoadDashboard'));
    } finally {
      setLoading(false);
    }
  }, [period, siteIds, currencyMode]);

  // Fetch on mount + period/site/currency change
  useEffect(() => { refreshData(); }, [refreshData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(refreshData, 30000);
    return () => clearInterval(interval);
  }, [refreshData]);

  const thresholds = { green: 5, yellow: 2 };

  const periodLabel = t(`periods.${period}`);

  // Opens DataPilot for a single KPI card, seeded with the current snapshot.
  const askMetric = (key: keyof typeof DASH_KPI_META, title: string, value: number, change: number) => {
    const meta = DASH_KPI_META[key];
    const entry = KPI_GLOSSARY.find((k) => k.id === meta.id);
    openForMetric({
      metricId: meta.id,
      metricName: title,
      entry,
      period: periodLabel,
      snapshot: {
        value,
        change_pct: change,
        unit: meta.unit,
        currency: meta.unit ? undefined : currencyMode === 'BASE' ? 'EUR' : currencyMode,
      },
    });
  };

  // Metric snapshots for the narrative banner (standard, non-BASE view).
  const narrativeMetrics: DPNamedSnapshot[] =
    currencyMode === 'BASE'
      ? []
      : [
          { metric_id: 'KPI-X01', metric_name: t('dashboard.grossGamingRevenue'), snapshot: { value: metrics.ggr.value, change_pct: metrics.ggr.change, currency: currencyMode } },
          { metric_id: 'KPI-X02', metric_name: t('dashboard.netGamingRevenue'), snapshot: { value: metrics.ngr.value, change_pct: metrics.ngr.change, currency: currencyMode } },
          { metric_id: 'KPI-C27', metric_name: t('dashboard.totalTurnover'), snapshot: { value: metrics.turnover.value, change_pct: metrics.turnover.change, currency: currencyMode } },
          { metric_id: 'KPI-C13', metric_name: t('dashboard.totalDeposits'), snapshot: { value: metrics.deposits.value, change_pct: metrics.deposits.change, currency: currencyMode } },
          { metric_id: 'KPI-C21', metric_name: t('dashboard.totalWithdrawals'), snapshot: { value: metrics.withdrawals.value, change_pct: metrics.withdrawals.change, currency: currencyMode } },
          { metric_id: 'KPI-C03', metric_name: t('dashboard.activePlayers'), snapshot: { value: metrics.activePlayers.value, change_pct: metrics.activePlayers.change, unit: 'players' } },
        ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{t('dashboard.title')}</h1>
          <p className="mt-1 text-slate-600 dark:text-slate-400">{t('dashboard.subtitle')}</p>
        </div>
        <button onClick={refreshData} disabled={loading}
          className="rounded-lg border border-slate-300 bg-white px-4 py-2 font-medium text-slate-900 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700">
          {loading ? t('dashboard.refreshing') : t('dashboard.refresh')}
        </button>
      </div>

      {/* Site Filter + Currency + Period Selector */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <SiteFilter selected={siteIds} onChange={setSiteIds} />
          <CurrencySelector selected={currencyMode} onChange={setCurrencyMode} />
        </div>
      </div>
      <div className="flex gap-2">
        {(['today', 'week', 'month', 'quarter'] as const).map((p) => (
          <button key={p} onClick={() => setPeriod(p)}
            className={`rounded-lg px-4 py-2 font-medium transition-colors ${
              period === p
                ? 'bg-accent text-white'
                : 'border border-slate-300 bg-white text-slate-900 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700'
            }`}>
            {t(`periods.${p}`)}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-900/20">
          <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* BASE mode note */}
      {currencyMode === 'BASE' && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/50 dark:bg-amber-900/20">
          <p className="text-xs text-amber-800 dark:text-amber-300">
            {t('dashboard.baseCurrencyNote')}
          </p>
        </div>
      )}

      {/* DataPilot narrative */}
      {!loading && narrativeMetrics.length > 0 && (
        <DashboardNarrative metrics={narrativeMetrics} period={periodLabel} />
      )}

      {/* KPI Cards Grid */}
      {currencyMode === 'BASE' && currencyMetrics.size > 0 ? (
        <div className="space-y-6">
          {/* Global Active Players total */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KPICard title={t('dashboard.totalActivePlayers')} value={formatNumber(metrics.activePlayers.value)} change={metrics.activePlayers.change} currency="" threshold={thresholds} />
          </div>

          {/* Per-currency KPIs */}
          {Array.from(currencyMetrics.entries()).map(([ccy, m]) => {
            const s = nativeSymbol(ccy);
            return (
              <div key={ccy}>
                <div className="mb-3 flex items-center gap-2">
                  <span className="rounded-md bg-slate-200 px-2.5 py-1 text-sm font-bold text-slate-800 dark:bg-slate-700 dark:text-slate-200">{ccy}</span>
                  <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <KPICard title="GGR" value={formatAmount(m.ggr.value)} change={m.ggr.change} currency={s} threshold={thresholds} />
                  <KPICard title="NGR" value={formatAmount(m.ngr.value)} change={m.ngr.change} currency={s} threshold={thresholds} />
                  <KPICard title="Turnover" value={formatAmount(m.turnover.value)} change={m.turnover.change} currency={s} threshold={thresholds} />
                  <KPICard title="Deposits" value={formatAmount(m.deposits.value)} change={m.deposits.change} currency={s} threshold={thresholds} />
                  <KPICard title="Withdrawals" value={formatAmount(m.withdrawals.value)} change={m.withdrawals.change} currency={s} threshold={thresholds} />
                  <KPICard title="Active Players" value={formatNumber(m.activePlayers.value)} change={m.activePlayers.change} currency="" threshold={thresholds} />
                  <KPICard
                    title="ARPU"
                    value={formatAmount(m.activePlayers.value > 0 ? m.ggr.value / m.activePlayers.value : 0)}
                    change={0}
                    currency={s}
                    threshold={thresholds}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KPICard title={t('dashboard.grossGamingRevenue')} value={formatAmount(metrics.ggr.value)} change={metrics.ggr.change} currency={symbol} threshold={thresholds} onAskAssistant={() => askMetric('ggr', t('dashboard.grossGamingRevenue'), metrics.ggr.value, metrics.ggr.change)} />
          <KPICard title={t('dashboard.netGamingRevenue')} value={formatAmount(metrics.ngr.value)} change={metrics.ngr.change} currency={symbol} threshold={thresholds} onAskAssistant={() => askMetric('ngr', t('dashboard.netGamingRevenue'), metrics.ngr.value, metrics.ngr.change)} />
          <KPICard title={t('dashboard.totalTurnover')} value={formatAmount(metrics.turnover.value)} change={metrics.turnover.change} currency={symbol} threshold={thresholds} onAskAssistant={() => askMetric('turnover', t('dashboard.totalTurnover'), metrics.turnover.value, metrics.turnover.change)} />
          <KPICard title={t('dashboard.totalDeposits')} value={formatAmount(metrics.deposits.value)} change={metrics.deposits.change} currency={symbol} threshold={thresholds} onAskAssistant={() => askMetric('deposits', t('dashboard.totalDeposits'), metrics.deposits.value, metrics.deposits.change)} />
          <KPICard title={t('dashboard.totalWithdrawals')} value={formatAmount(metrics.withdrawals.value)} change={metrics.withdrawals.change} currency={symbol} threshold={thresholds} onAskAssistant={() => askMetric('withdrawals', t('dashboard.totalWithdrawals'), metrics.withdrawals.value, metrics.withdrawals.change)} />
          <KPICard title={t('dashboard.activePlayers')} value={formatNumber(metrics.activePlayers.value)} change={metrics.activePlayers.change} currency="" threshold={thresholds} onAskAssistant={() => askMetric('activePlayers', t('dashboard.activePlayers'), metrics.activePlayers.value, metrics.activePlayers.change)} />
          <KPICard
            title="ARPU"
            value={formatAmount(metrics.activePlayers.value > 0
              ? metrics.ggr.value / metrics.activePlayers.value
              : 0)}
            change={0}
            currency={symbol}
            threshold={thresholds}
            onAskAssistant={() => askMetric('arpu', 'ARPU', metrics.activePlayers.value > 0 ? metrics.ggr.value / metrics.activePlayers.value : 0, 0)}
          />
        </div>
      )}

      {/* Time Series Chart */}
      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <h2 className="mb-6 text-lg font-bold text-slate-900 dark:text-white">{t('dashboard.dailyGgrTrend')}</h2>
        {timeSeriesData.length === 0 ? (
          <p className="py-16 text-center text-slate-500">{t('dashboard.noTimeSeriesData')}</p>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={timeSeriesData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
              <XAxis dataKey="date" stroke="#666" style={{ fontSize: '12px' }} />
              <YAxis stroke="#666" style={{ fontSize: '12px' }} />
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }}
                formatter={(value: number) => `${symbol}${formatNumber(value)}`} />
              <Legend />
              <Line type="monotone" dataKey="ggr" stroke="#009EA0" strokeWidth={2} dot={{ fill: '#009EA0', r: 5 }} activeDot={{ r: 7 }} />
              <Line type="monotone" dataKey="ngr" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 5 }} activeDot={{ r: 7 }} />
              <Line type="monotone" dataKey="turnover" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', r: 5 }} activeDot={{ r: 7 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Info Box */}
      <div className="rounded-lg border border-accent-200 bg-accent-50 p-4 dark:border-accent-900/50 dark:bg-accent-900/20">
        <p className="text-sm text-accent-800 dark:text-accent-200">
          {t('dashboard.autoRefreshInfo')} {currencyMode}. {t('dashboard.lastUpdated')} {new Date().toLocaleTimeString()}
        </p>
      </div>
    </div>
  );
};
